import React, { useState, useEffect, useRef } from "react";
import { isPromoActive } from "./PromoBanner";
import { VideoRecorder } from "./VideoRecorder";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { X, Loader2, CreditCard } from "lucide-react";
import { toast } from "../hooks/use-toast";

export default function CheckoutModal({ slug, product, promo, videoEnabled = true, scheduleEnabled = true, smsEnabled = false, onClose }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "", recipient_name: "", recipient_email: "", recipient_phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null); // {price_paid, saving, code}
  const [checking, setChecking] = useState(false);
  const [sendDate, setSendDate] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pricePaid = discount ? discount.price_paid : product.price;
  const promoApplied = useRef(false);

  const applyCode = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : discountCode).trim();
    if (!code) return;
    setChecking(true);
    try {
      const { data } = await api.post(`/store/${slug}/apply-discount`, { code, product_id: product.id });
      if (data.valid) {
        setDiscount(data);
        toast({ title: "Discount applied", description: `You save £${data.saving.toFixed(2)}!` });
      } else {
        setDiscount(null);
        toast({ title: "Invalid code", description: data.message || "Not valid", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Could not apply code", variant: "destructive" });
    } finally { setChecking(false); }
  };

  useEffect(() => {
    if (promoApplied.current || !isPromoActive(promo) || !promo.discount_code) return;
    promoApplied.current = true;
    setDiscountCode(promo.discount_code.toUpperCase());
    applyCode(promo.discount_code.toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo]);

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/store/${slug}/checkout`, {
        product_id: product.id, ...form, discount_code: discount ? discount.code : "",
        send_date: sendDate, origin: window.location.origin, video_url: videoUrl,
      });
      if (data.mode === "stripe" && data.url) {
        if (window.self !== window.top) { if (!window.open(data.url, "_blank")) window.top.location.href = data.url; }
        else window.location.href = data.url; // redirect to Stripe hosted checkout
        return;
      }
      // mock mode -> voucher already created
      if (data.scheduled) {
        toast({ title: "Voucher scheduled", description: `It will be emailed to the recipient on ${sendDate}.` });
      } else {
        toast({ title: "Payment successful", description: "Your gift voucher is ready!" });
      }
      nav(`/voucher/${data.code}?new=1`);
    } catch (err) {
      toast({ title: "Purchase failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-[#16243b] text-lg">{product.title}</h3>
            <p className="text-[#2ec9c9] font-bold text-xl">£{product.price.toFixed(2)}</p>
          </div>
          <button onClick={onClose}><X className="text-[#5a6b80]" /></button>
        </div>
        <form onSubmit={pay} className="p-6 space-y-4">
          {[["Your name", "buyer_name", "text"], ["Your email", "buyer_email", "email"], ["Your mobile number", "buyer_phone", "tel"], ["Recipient name (optional)", "recipient_name", "text"], ["Recipient email (voucher is sent here)", "recipient_email", "email"]].map(([l, k, t]) => (
            <div key={k}>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">{l}</label>
              <input type={t} required={k === "buyer_name" || k === "buyer_email" || k === "buyer_phone"} value={form[k]} onChange={set(k)}
                data-testid={`checkout-${k.replace("_", "-")}`}
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition" />
            </div>
          ))}
          {smsEnabled && (
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Recipient mobile (optional — we'll text them the code)</label>
              <input type="tel" value={form.recipient_phone} onChange={set("recipient_phone")} placeholder="07700 900123" data-testid="checkout-recipient-phone"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Personal message (optional)</label>
            <textarea value={form.message} onChange={set("message")} rows={2}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition resize-none" />
          </div>
          {videoEnabled && <VideoRecorder slug={slug} value={videoUrl} onChange={setVideoUrl} />}
          {scheduleEnabled && <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Schedule delivery (optional)</label>
            <input type="date" min={today} value={sendDate} onChange={(e) => setSendDate(e.target.value)}
              data-testid="checkout-schedule-date"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition" />
            {sendDate && sendDate > today && (
              <p className="text-sm text-[#2ec9c9] mt-2 font-medium">The voucher will be emailed to the recipient on {sendDate}.</p>
            )}
          </div>}
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Discount code (optional)</label>
            <div className="flex gap-2">
              <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="checkout-discount-input"
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] uppercase font-mono" />
              <button type="button" onClick={applyCode} disabled={checking} data-testid="checkout-apply-discount" className="gp-btn-outline rounded-xl px-4 font-medium disabled:opacity-60">
                {checking ? "…" : "Apply"}
              </button>
            </div>
            {discount && (
              <p className="text-sm text-green-600 mt-2 font-medium" data-testid="checkout-discount-applied">Code {discount.code} applied — you save £{discount.saving.toFixed(2)}! Recipient still gets the full £{product.price.toFixed(2)} value.</p>
            )}
          </div>
          <div className="bg-[#f6f8fb] rounded-xl p-4 text-sm text-[#5a6b80] flex items-start gap-2">
            <CreditCard size={18} className="text-[#2ec9c9] mt-0.5 shrink-0" />
            <span><strong className="text-[#16243b]">Secure checkout</strong> — your payment is processed securely.</span>
          </div>
          <button disabled={loading} data-testid="checkout-pay-btn" className="gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={18} /> : null}
            {loading ? "Processing…" : `Pay £${pricePaid.toFixed(2)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
